//@flow
import React, { Component, Children } from "react"
import { Motion, spring } from "react-motion"
import GridItem from "./GridItem"
import GridContainer from "./GridContainer"
import { reinsert, layout, clamp } from "./helpers"
import type { Props, State, PageCoord } from "./types"

const springSetting1 = { stiffness: 180, damping: 10 }
const springSetting2 = { stiffness: 120, damping: 17 }

class GridView extends Component<Props, State> {
  _containerTarget: ?HTMLElement

  constructor(props: Props) {
    super(props)
    this._containerTarget = null
    this.state = {
      containerWidth: 0,
      mouseXY: [0, 0],
      mouseDelta: [0, 0], // difference between mouse and item pos for x + y coords, for dragging
      lastPress: null, // key of the last pressed component
      isPressed: false,
      items: this.mapChildKeysFrom(props) || []
    }
  }

  mapChildKeysFrom(props: Props) {
    const { children } = props
    return Children.map(children, child => child.key)
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setState(() => ({ items: this.mapChildKeysFrom(nextProps) }))
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleWindowResize)
    this._containerTarget &&
      this._containerTarget.addEventListener("touchmove", this.handleTouchMove)
    this._containerTarget &&
      this._containerTarget.addEventListener("touchend", this.handleMouseUp)
    this._containerTarget &&
      this._containerTarget.addEventListener("mousemove", this.handleMouseMove)
    this._containerTarget &&
      this._containerTarget.addEventListener("mouseup", this.handleMouseUp)
    this.handleWindowResize()
  }

  compnentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowResize)
    this._containerTarget &&
      this._containerTarget.removeEventListener(
        "touchmove",
        this.handleTouchMove
      )
    this._containerTarget &&
      this._containerTarget.removeEventListener("touchend", this.handleMouseUp)
    this._containerTarget &&
      this._containerTarget.removeEventListener(
        "mousemove",
        this.handleMouseMove
      )
    this._containerTarget &&
      this._containerTarget.removeEventListener("mouseup", this.handleMouseUp)
  }

  handleWindowResize = () => {
    const { containerWidth } = this.state
    const width = this._containerTarget
      ? this._containerTarget.offsetWidth
      : containerWidth
    if (width > 0) {
      this.setState(() => ({
        containerWidth: width
      }))
    }
  }

  handleTouchStart = (
    key: string,
    pressLocation: Array<number>,
    e: TouchEvent
  ) => {
    this.handleMouseDown(key, pressLocation, e.touches[0])
  }

  handleTouchMove = (e: TouchEvent) => {
    e.preventDefault()
    this.handleMouseMove(e.touches[0])
  }

  handleMouseMove = ({ pageX, pageY }: PageCoord) => {
    const {
      containerWidth,
      lastPress,
      isPressed,
      mouseDelta: [dx, dy],
      items
    } = this.state
    const { itemWidth, itemHeight, staticChildCount } = this.props
    if (isPressed) {
      const count = items.length
      const cols = containerWidth / itemWidth
      const rows = Math.ceil(count / cols)
      const mouseXY = [pageX - dx, pageY - dy]
      const col = clamp(Math.floor(mouseXY[0] / itemWidth), 0, cols)
      const row = clamp(
        Math.floor(mouseXY[1] / itemHeight),
        0,
        Math.floor(rows)
      )
      const index = Math.min(row * cols + col, count - staticChildCount - 1)
      if (lastPress) {
        const newOrder = reinsert(items, items.indexOf(lastPress), index)
        this.setState(() => ({ mouseXY, items: newOrder }))
      }
    }
  }

  handleMouseDown = (
    key: number | string,
    [pressX, pressY]: Array<number>,
    { pageX, pageY }: PageCoord
  ) => {
    this.setState(() => ({
      lastPress: `${key}`,
      isPressed: true,
      mouseDelta: [pageX - pressX, pageY - pressY],
      mouseXY: [pressX, pressY]
    }))
  }

  handleMouseUp = (e: Event) => {
    const { onRepositioned, staticChildCount } = this.props
    const { items } = this.state
    onRepositioned(
      items
        .filter((i, idx) => idx < items.length - staticChildCount)
        .map((id, index) => ({ id, position: index }))
    )
    this.setState({ isPressed: false, mouseDelta: [0, 0] })
  }

  render() {
    const {
      itemWidth,
      itemHeight,
      children,
      staticChildCount,
      gutter
    } = this.props
    const { containerWidth, items, isPressed, lastPress, mouseXY } = this.state
    const cols = Math.floor((containerWidth + gutter) / itemWidth)
    const height =
      Math.ceil(Children.count(children) / cols) * (itemHeight + gutter)
    return (
      <GridContainer
        innerRef={el => (this._containerTarget = el)}
        height={height}
      >
        {containerWidth === 0 ? (
          ""
        ) : (
          Children.map(children, child => {
            const key = `${child.key || ""}`
            let style
            let x
            let y

            const visualPosition = items.indexOf(key)
            const isDraggable = visualPosition < items.length - staticChildCount
            if (key === lastPress && isPressed) {
              ;[x, y] = mouseXY
              style = {
                translateX: x,
                translateY: y,
                scale: spring(1.2, springSetting1)
              }
            } else {
              ;[x, y] = layout(
                visualPosition,
                itemWidth,
                itemHeight,
                cols,
                gutter
              )
              style = {
                translateX: spring(x, springSetting2),
                translateY: spring(y, springSetting2),
                scale: spring(1, springSetting1)
              }
            }

            return (
              <Motion key={key} style={style}>
                {({ translateX, translateY, scale, boxShadow }) => (
                  <GridItem
                    key={key}
                    width={itemWidth}
                    height={itemHeight}
                    style={{
                      WebkitTransform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
                      transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`,
                      zIndex: key === lastPress ? 99 : visualPosition
                    }}
                    onMouseDown={
                      isDraggable &&
                      this.handleMouseDown.bind(null, key, [x, y])
                    }
                    onTouchStart={
                      isDraggable &&
                      this.handleTouchStart.bind(null, key, [x, y])
                    }
                  >
                    {child}
                  </GridItem>
                )}
              </Motion>
            )
          })
        )}
      </GridContainer>
    )
  }
}

export default GridView



// WEBPACK FOOTER //
// ./src/UI/GridView/GridView.js